import { scope, Traversal, type } from 'arktype';

import { addressSchema, swiftAddressSchema } from '@/schemas/address';
import { validIbanOrAccountNumber } from '@/schemas/utils';
import { BankAccountType } from '@/types/bankAccountType';
import { CopDocumentType } from '@/types/copDocumentType';
import { CountryType } from '@/types/country';
import { CurrencyCodeType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { PaymentMethodAccountType } from '@/types/paymentMethodAccountType';

export const fiatAchPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.enumerated(CountryType.US).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.USD).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.ach),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: addressSchema,
  routingNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
}).readonly();

export const fiatWirePaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.enumerated(CountryType.US).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.USD).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.wire),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: addressSchema,
  routingNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  accountNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
}).readonly();

export const fiatPixPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.pix),
  country: type.enumerated(CountryType.BR).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.BRL).as<CurrencyCodeType>(),
  pixKey: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  documentNumber: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatSpeiPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.spei),
  country: type.enumerated(CountryType.MX).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.MXN).as<CurrencyCodeType>(),
  clabe: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  receiverName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCvuPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  paymentMethod: type.enumerated(FiatAccountType.cvu),
  country: type.enumerated(CountryType.AR).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.ARS).as<CurrencyCodeType>(),
  cvu: type('string')
    .narrow((cvu: string): cvu is string => {
      if (cvu.length !== 22) {
        return false;
      }
      return /^\d{22}$/.test(cvu);
    })
    .configure({ message: 'VALIDATION_CVU_INVALID' }),
  recipientName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

const fiatSwiftPaymentMethodBaseSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.valueOf(CountryType).as<CountryType>(),
  currency: type.valueOf(CurrencyCodeType).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.swift),
  bankName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  address: swiftAddressSchema,
  swiftCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  iban: type('string > 0')
    .configure({ message: 'VALIDATION_REQUIRED' })
    .narrow(validIbanOrAccountNumber),
  isPobo: 'boolean',
  'registrationNumber?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'bankCode?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'mobileNumber?': type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
});

const requireZipCodeWhenNotHK = (
  data: typeof fiatSwiftPaymentMethodBaseSchema.infer,
  ctx: Traversal,
): boolean => {
  if (data.country !== CountryType.HK && data.address.zipCode.length === 0) {
    return ctx.reject({
      path: ['address', 'zipCode'],
      message: 'VALIDATION_REQUIRED',
    });
  }
  return true;
};

export const fiatSwiftPaymentMethodSchema = fiatSwiftPaymentMethodBaseSchema
  .narrow(requireZipCodeWhenNotHK)
  .readonly();

export const fiatSepaPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  country: type.valueOf(CountryType).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.EUR).as<CurrencyCodeType>(),
  paymentMethod: type.enumerated(FiatAccountType.sepa),
  iban: type('string > 0')
    .configure({ message: 'VALIDATION_REQUIRED' })
    .narrow(validIbanOrAccountNumber),
  'address?': 'undefined',
}).readonly();

export const fiatCOPBeneficiaryIndividualPaymentMethodSchema = type({
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  lastName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'individual'").configure({ message: 'VALIDATION_REQUIRED' }),
  name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCOPBeneficiaryCompanyPaymentMethodSchema = type({
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'company'").configure({ message: 'VALIDATION_REQUIRED' }),
  // name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCOPThirdPartyIndividualPaymentMethodSchema = type({
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  lastName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'individual'").configure({ message: 'VALIDATION_REQUIRED' }),
  // name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  thirdPartyWithdrawal: 'true',
  documentId: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  documentType: type.valueOf(CopDocumentType),
  email: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCOPThirdPartyCompanyPaymentMethodSchema = type({
  firstName: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  type: type.enumerated(PaymentMethodAccountType.fiat),
  recipientType: type("'company'").configure({ message: 'VALIDATION_REQUIRED' }),
  // name: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  paymentMethod: type.enumerated(FiatAccountType.cop),
  country: type.enumerated(CountryType.CO).as<CountryType>(),
  currency: type.enumerated(CurrencyCodeType.COP).as<CurrencyCodeType>(),
  bankAccount: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  bankType: type.valueOf(BankAccountType),
  bankCode: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  thirdPartyWithdrawal: 'true',
  documentId: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  documentType: type.valueOf(CopDocumentType),
  email: type('string > 0').configure({ message: 'VALIDATION_REQUIRED' }),
  'address?': 'undefined',
}).readonly();

export const fiatCopPaymentMethodSchema = scope({
  FiatCOPBeneficiaryIndividualPayment: fiatCOPBeneficiaryIndividualPaymentMethodSchema,
  FiatCOPBeneficiaryCompanyPayment: fiatCOPBeneficiaryCompanyPaymentMethodSchema,
  FiatCOPThirdPartyIndividualPayment: fiatCOPThirdPartyIndividualPaymentMethodSchema,
  FiatCOPThirdPartyCompanyPayment: fiatCOPThirdPartyCompanyPaymentMethodSchema,
})
  .type(
    'FiatCOPBeneficiaryIndividualPayment | FiatCOPBeneficiaryCompanyPayment | FiatCOPThirdPartyIndividualPayment | FiatCOPThirdPartyCompanyPayment',
  )
  .readonly();

export const paymentMethodFiatSchema = scope({
  FiatACHPayment: fiatAchPaymentMethodSchema,
  FiatWirePayment: fiatWirePaymentMethodSchema,
  FiatPIXPayment: fiatPixPaymentMethodSchema,
  FiatSwiftPayment: fiatSwiftPaymentMethodSchema,
  FiatSepaPayment: fiatSepaPaymentMethodSchema,
  FiatCOPPayment: fiatCopPaymentMethodSchema,
  FiatSpeiPayment: fiatSpeiPaymentMethodSchema,
  FiatCvuPayment: fiatCvuPaymentMethodSchema,
})
  .type(
    'FiatACHPayment | FiatWirePayment | FiatPIXPayment | FiatSwiftPayment | FiatSepaPayment | FiatCOPPayment | FiatSpeiPayment | FiatCvuPayment',
  )
  .readonly();

export type FiatACHPaymentForm = typeof fiatAchPaymentMethodSchema.infer;
export type FiatWirePaymentForm = typeof fiatWirePaymentMethodSchema.infer;
export type FiatPixPaymentForm = typeof fiatPixPaymentMethodSchema.infer;
export type FiatSwiftPaymentForm = typeof fiatSwiftPaymentMethodSchema.infer;
export type FiatSepaPaymentForm = typeof fiatSepaPaymentMethodSchema.infer;
export type FiatSpeiPaymentForm = typeof fiatSpeiPaymentMethodSchema.infer;
export type FiatCOPBeneficiaryIndividualPaymentForm =
  typeof fiatCOPBeneficiaryIndividualPaymentMethodSchema.infer;
export type FiatCOPBeneficiaryCompanyPaymentForm =
  typeof fiatCOPBeneficiaryCompanyPaymentMethodSchema.infer;
export type FiatCOPThirdPartyIndividualPaymentForm =
  typeof fiatCOPThirdPartyIndividualPaymentMethodSchema.infer;
export type FiatCOPThirdPartyCompanyPaymentForm =
  typeof fiatCOPThirdPartyCompanyPaymentMethodSchema.infer;
export type FiatCOPPaymentForm = typeof fiatCopPaymentMethodSchema.infer;
export type FiatCvuPaymentForm = typeof fiatCvuPaymentMethodSchema.infer;

export type FiatPaymentForm = typeof paymentMethodFiatSchema.infer;

export type PaymentMethodForm = FiatPaymentForm;

export const isFiatACHPaymentForm = (value: PaymentMethodForm): value is FiatACHPaymentForm =>
  fiatAchPaymentMethodSchema.allows(value);

export const isFiatWirePaymentForm = (value: PaymentMethodForm): value is FiatWirePaymentForm =>
  fiatWirePaymentMethodSchema.allows(value);

export const isFiatPixPaymentForm = (value: PaymentMethodForm): value is FiatPixPaymentForm =>
  fiatPixPaymentMethodSchema.allows(value);

export const isFiatSwiftPaymentForm = (value: PaymentMethodForm): value is FiatSwiftPaymentForm =>
  fiatSwiftPaymentMethodSchema.allows(value);

export const isFiatSepaPaymentForm = (value: PaymentMethodForm): value is FiatSepaPaymentForm =>
  fiatSepaPaymentMethodSchema.allows(value);

export const isFiatSpeiPaymentForm = (value: PaymentMethodForm): value is FiatSpeiPaymentForm =>
  fiatSpeiPaymentMethodSchema.allows(value);

export const isFiatCOPBeneficiaryPaymentForm = (
  value: PaymentMethodForm,
): value is FiatCOPBeneficiaryIndividualPaymentForm | FiatCOPBeneficiaryCompanyPaymentForm =>
  fiatCOPBeneficiaryIndividualPaymentMethodSchema.allows(value) ||
  fiatCOPBeneficiaryCompanyPaymentMethodSchema.allows(value);

export const isFiatCOPThirdPartyPaymentForm = (
  value: PaymentMethodForm,
): value is FiatCOPThirdPartyIndividualPaymentForm | FiatCOPThirdPartyCompanyPaymentForm =>
  fiatCOPThirdPartyIndividualPaymentMethodSchema.allows(value) ||
  fiatCOPThirdPartyCompanyPaymentMethodSchema.allows(value);

export const isFiatCOPPaymentForm = (value: PaymentMethodForm): value is FiatCOPPaymentForm =>
  fiatCopPaymentMethodSchema.allows(value);

export const isFiatCvuPaymentForm = (value: PaymentMethodForm): value is FiatCvuPaymentForm =>
  fiatCvuPaymentMethodSchema.allows(value);
